import { useState, useRef, type KeyboardEvent } from "react";
import {
  Badge,
  Box,
  Button,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
  Loader,
} from "@mantine/core";
import { IconFileTypePdf, IconSparkles } from "@tabler/icons-react";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { getCollaborationRoleLabel } from "@/components/shared/constants/general.const";
import { downloadElementAsPdf } from "@/utils/expressInterestPreviewPdf";
import { notifications } from "@mantine/notifications";

type PreviewOption = {
  title: string;
  summary: string;
  requesterRole: string;
  expectedPartnerRole: string;
};

type PlatformCompanyMeta = { id: string; gtid: string; name: string; inputType: "gtid" };
type OffPlatformCompanyMeta = {
  websiteUrl?: string;
  gtid?: string;
  name: string;
  inputType: "gtid" | "url";
};

function companyPreviewLabel(
  meta: OffPlatformCompanyMeta | PlatformCompanyMeta | null | undefined,
): string {
  if (!meta) return "—";
  if (meta.inputType === "gtid") return meta.gtid?.trim() || "—";
  return meta.name?.trim() || "—";
}

export default function AdminExpressInterestPreviewPage() {
  const [companyAWebsiteUrl, setCompanyAWebsiteUrl] = useState("");
  const [companyBGtidOrId, setCompanyBGtidOrId] = useState("");
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [options, setOptions] = useState<PreviewOption[]>([]);
  const [platformCompany, setPlatformCompany] = useState<PlatformCompanyMeta | null>(null);
  const [offPlatformCompany, setOffPlatformCompany] = useState<OffPlatformCompanyMeta | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const pdfExportRef = useRef<HTMLDivElement>(null);

  const runPreview = async () => {
    setLoading(true);
    setOptions([]);
    setPlatformCompany(null);
    setOffPlatformCompany(null);
    setSelectedIndex(null);
    try {
      const res = await trpc.admin.previewExpressInterestFromWebsites.mutate({
        companyAWebsiteUrl: companyAWebsiteUrl.trim(),
        companyBGtidOrId: companyBGtidOrId.trim(),
      });
      setOptions(res.options ?? []);
      setPlatformCompany(res.platformCompany ?? null);
      setOffPlatformCompany(res.offPlatformCompany ?? null);
    } catch (e) {
      handleTrpcError(e);
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    const el = pdfExportRef.current;
    if (!el || options.length === 0) return;
    setPdfLoading(true);
    try {
      const stamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
      await downloadElementAsPdf(el, `express-interest-preview-${stamp}`);
    } catch (e) {
      console.error(e);
      notifications.show({
        title: "PDF export failed",
        message: e instanceof Error ? e.message : "Could not create PDF",
        color: "red",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const cardKeyDown = (e: KeyboardEvent, index: number) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setSelectedIndex(index);
    }
  };

  const canSubmit =
    companyAWebsiteUrl.trim().length > 0 && companyBGtidOrId.trim().length > 0 && !loading;

  return (
    <Stack p="lg" gap="lg" maw={1400}>
      <div>
        <Title order={2}>Express interest preview (admin)</Title>
        <Text size="sm" c="dimmed" mt={6}>
          Preview express interest from <strong>Company A</strong> (website URL or platform GTID/ID) toward{" "}
          <strong>Company B</strong> (platform GTID or ID). Copy names Company A from its profile and refers to
          Company B by <strong>GTID</strong>. Member express-interest is unchanged.
        </Text>
      </div>

      <Paper withBorder p="md" radius="md">
        <Stack gap="md">
          <TextInput
            label="Company A — website URL or GTID (requester)"
            description="External site URL (crawled) or a platform GTID/company ID (loaded from the database)."
            placeholder="https://example.com or GT-XXXXXX"
            value={companyAWebsiteUrl}
            onChange={(e) => setCompanyAWebsiteUrl(e.target.value)}
          />
          <TextInput
            label="Company B — GTID or company ID (on platform, target)"
            description="GTsetu company (target); profile loaded from the database."
            placeholder="GT-XXXXXX or company UUID"
            value={companyBGtidOrId}
            onChange={(e) => setCompanyBGtidOrId(e.target.value)}
          />
          <Group>
            <Button leftSection={<IconSparkles size={18} />} onClick={() => void runPreview()} disabled={!canSubmit}>
              Generate suggestions
            </Button>
            <Button
              variant="light"
              color="red"
              leftSection={<IconFileTypePdf size={18} />}
              onClick={() => void downloadPdf()}
              disabled={options.length === 0 || pdfLoading}
              loading={pdfLoading}
            >
              Download PDF
            </Button>
          </Group>
        </Stack>
      </Paper>

      {loading && (
        <Group justify="center" py="xl" gap="md">
          <Loader size="md" />
          <Text size="sm" c="dimmed">
            Loading companies and extracting website data when needed (can take a minute)…
          </Text>
        </Group>
      )}

      {options.length > 0 && (
        <Text size="sm" c="dimmed">
          {selectedIndex !== null
            ? `Selected: option ${selectedIndex + 1} of ${options.length}. Click another card to change.`
            : "Click a card to select an option."}
        </Text>
      )}

      {options.length > 0 && (
        <Box
          ref={pdfExportRef}
          bg="#fff"
          p={{ base: "md", sm: "lg" }}
          style={{
            borderRadius: 8,
            border: "1px solid #e9ecef",
          }}
        >
          <Stack gap="lg" pb="md">
            <Box>
              <Group gap="md" align="center" wrap="wrap" mb={6}>
                <Title order={3} size="h4" m={0}>
                  Express interest preview
                </Title>
                <Badge color="primary" variant="filled" size="sm">
                  GTsetu AI
                </Badge>
              </Group>
              <Text size="sm" fw={600} c="slate.8">
                Requester (Company A): {companyPreviewLabel(offPlatformCompany)}
              </Text>
              {offPlatformCompany?.inputType === "url" && offPlatformCompany.websiteUrl ? (
                <Text size="xs" c="dimmed">
                  Website: {offPlatformCompany.websiteUrl}
                </Text>
              ) : null}
              <Text size="sm" c="dimmed">
                Target (Company B): {companyPreviewLabel(platformCompany)}
              </Text>
              <Text size="xs" c="dimmed" mt={6}>
                Generated {new Date().toLocaleString()}
                {selectedIndex !== null ? ` · Selection: option ${selectedIndex + 1}` : ""}
              </Text>
            </Box>

            <SimpleGrid
              cols={{ base: 1, sm: 2 }}
              spacing="md"
              styles={{ root: { alignItems: "stretch" } }}
            >
              {options.map((opt, index) => {
                const isSelected = selectedIndex === index;
                return (
                  <Paper
                    key={`${opt.title}-${index}`}
                    withBorder
                    p="md"
                    radius="md"
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedIndex(index)}
                    onKeyDown={(e) => cardKeyDown(e, index)}
                    aria-pressed={isSelected}
                    aria-label={`Option ${index + 1}: ${opt.title}${isSelected ? ", selected" : ""}`}
                    styles={{
                      root: {
                        cursor: "pointer",
                        outline: "none",
                        boxShadow: isSelected
                          ? "0 0 0 2px var(--mantine-color-primary-6), 0 4px 12px rgba(4, 48, 92, 0.12)"
                          : undefined,
                        borderColor: isSelected ? "var(--mantine-color-primary-6)" : undefined,
                        borderWidth: isSelected ? 2 : 1,
                        backgroundColor: isSelected ? "var(--mantine-color-primary-0)" : undefined,
                        transition:
                          "border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease",
                        breakInside: "avoid",
                      },
                    }}
                  >
                    <Badge
                      size="sm"
                      variant="light"
                      color="primary"
                      mb="xs"
                      styles={{
                        root: isSelected
                          ? {
                              border: "2px solid var(--mantine-color-primary-7)",
                              fontWeight: 600,
                            }
                          : undefined,
                      }}
                    >
                      Option {index + 1}
                    </Badge>
                    <Text fw={700} size="md" mb="xs">
                      {opt.title}
                    </Text>
                    <Text size="sm" c="dimmed" style={{ whiteSpace: "pre-wrap" }}>
                      {opt.summary}
                    </Text>
                    <Box mt="md">
                      <Text size="xs" c="dimmed">
                        Requester role
                      </Text>
                      <Text size="sm">{getCollaborationRoleLabel(opt.requesterRole)}</Text>
                      <Text size="xs" c="dimmed" mt={8}>
                        Expected partner role
                      </Text>
                      <Text size="sm">{getCollaborationRoleLabel(opt.expectedPartnerRole)}</Text>
                    </Box>
                  </Paper>
                );
              })}
            </SimpleGrid>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
